<template>
  <div
    class="forum-header-wrapper"
    :style="headerStyle"
  >
    <div class="forum-header">
      <!-- <Poptip placement="bottom" class="forum-menu">
        <Button class="menu-button">
          <i class="fas fa-bars"></i>
        </Button>
        <div slot="content" class="menu-content">
          <div @click="routeToForum" class="menu-route-forum"> Forum </div>
        </div>
      </Poptip> -->
      <div class="header-icons-wrapper">
        <Tooltip
          content="Back to Data Portal"
          placement="bottom"
          class="header-icon"
        >
          <i
            class="fas fa-arrow-circle-left"
            @click="routeToPage('Metrics Summary')"
          />
        </Tooltip>
        <Tooltip
          content="Forum Homepage"
          placement="bottom"
          class="header-icon"
        >
          <i
            class="fas fa-home"
            @click="routeToPage('Forum')"
          />
        </Tooltip>
        <Tooltip
          content="Create A New Post"
          placement="bottom"
          class="header-icon"
        >
          <i
            class="fas fa-plus-circle"
            @click="onOpenNewPostModal"
          />
        </Tooltip>
        <Tooltip
          content="Forum Help"
          placement="bottom"
          class="header-icon"
        >
          <a
            :href="helpPageLink"
            target="_blank"
          >
            <i
              class="fas fa-question-circle"
              style="color: white;"
            />
          </a>
        </Tooltip>
      </div>
      <Form
        class="forum-search"
        @submit.native.prevent
      >
        <FormItem>
          <Input
            v-model="filterText"
            type="text"
            placeholder="Search"
            search
            clearable
            class="search-box"
            @on-search="onSearchForum"
            @on-clear="onSearchClear"
            @on-change="onChangeReact"
          />
        </FormItem>
      </Form>
    </div>
    <div class="new-post-wrapper">
      <NewPost
        ref="newPost"
        v-model="showNewPostModal"
        :fullscreen="showNewPostModalFullScreen"
        :default-content-html="newPostDefaultContentHtml"
        @show-fullscreen="onShowNewPostModalFullScreen"
      />
    </div>
  </div>
</template>

<script>
import NewPost from './NewPost'
import { mapState } from 'vuex'
import UserMixin from '@/mixins/UserMixin'

export default {
  components: {
    NewPost
  },

  mixins: [
    UserMixin
  ],

  props: {
    hasNotice: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      filterText: this.$route.query.searchText || '',
      showNewPostModal: false,
      showNewPostModalFullScreen: false,
      newPostDefaultContentHtml: ''
    }
  },

  computed: {
    headerStyle () {
      return {
        '--forum-header-moveup': this.hasNotice ? '-72px' : '-48px'
      }
    },

    ...mapState('forum', {
      searchText: 'searchText',
      forumTags: 'forumTags'
    }),

    helpPageLink () {
      return `${process.env.VUE_APP_HELP_PAGE_URL}/forum/index.html`
    }
  },

  beforeCreate () {
    if (!this.$root.preferences.hasOwnProperty('forum')) {
      this.$set(this.$root.preferences, 'forum', {})
    }

    sessionStorage.setItem('context', 'forum')
  },

  async created () {
    if (!this.forumTags.length) {
      this.$store.dispatch('forum/fetchForumTags')
    }

    // Update "filterText" var in case there's any value stored into SessionStorage.
    if (this.$root.preferences.forum.searchText) {
      // If there's no value on VueX "searchText" var, then update it with the existing one on SessionStorage.
      if (!this.searchText) {
        this.$store.commit('forum/updateSearchText', { searchText: this.$root.preferences.forum.searchText })
      }

      // Update the "filterText" value with the existing one on VueX.
      this.filterText = this.searchText

      // If the current module is "Forum" and it doesn't have the searchText param, then add it.
      if (this.$route.name === 'Forum') {
        this.onSearchForum()
      }
    }

    await this.fetchAllUsers()
    if (this.$route.query.feedback) {
      this.onSetDataForFeedback()
    }
  },

  methods: {
    /**
     * Route from current page to Forum Homepage
     */
    routeToPage (pageName) {
      this.$router.push({
        name: pageName
      })
    },

    /**
     * Triggered when user press enter in the serach box
     */
    onSearchForum () {
      this.$store.commit('forum/updateSearchText', { searchText: this.filterText })
      this.$set(this.$root.preferences.forum, 'searchText', this.filterText)
      delete this.$route.query.page
      this.$router.replace({
        path: '/forum',
        query: { ...this.$route.query, searchText: this.filterText }
      })
    },

    /**
     * Triggered when user clear the search text in the search box
     * Will remove the filtering then
     */
    onSearchClear () {
      this.$store.commit('forum/updateSearchText', { searchText: undefined })
      this.$set(this.$root.preferences.forum, 'searchText', undefined)
      delete this.$route.query.page
      this.$router.replace({
        path: '/forum',
        query: { ...this.$route.query, searchText: undefined }
      })
    },

    /**
     * Show new post modal in fullscreen when trigger event "showFullscreen"
     * Force re-render the modal component
     * TODO: make the modal centerized or with better positioning
     */
    onShowNewPostModalFullScreen (value) {
      this.showNewPostModalFullScreen = value
    },

    /**
     * Show new post modal when trigger click on the icon
     */
    onOpenNewPostModal () {
      this.$router.replace({
        ...this.$router,
        query: { ...this.$route.query, newpost: true }
      })
      this.showNewPostModal = true
    },

    /**
     * React to the change of filterText
     * Remove the search filter when all text is deleted
     */
    onChangeReact () {
      if (!this.filterText) {
        this.onSearchClear()
      }
    },

    /**
     * Deal with query backfilling (routed from $feedback)
     */
    onSetDataForFeedback () {
      const query = this.$route.query
      // Add reference link for the feedback and Add mentions for problem owners
      this.onAutoLink(query)
      this.onAutoMention(query)
      this.showNewPostModal = Boolean(query.newpost)
      this.showNewPostModalFullScreen = Boolean(query.fullscreen)
    },

    onAutoLink (query) {
      this.newPostDefaultContentHtml += `<p>Reference: <a href="${query.targetUrl}" rel="noopener noreferrer nofollow">${query.title}</a></p>`
    },

    onAutoMention (query) {
      const emails = query.receiverEmail
      const userToMention = this.users.filter(user => emails.includes(user.email) && user.email !== 'ruben.kogel@li.me')
      if (!userToMention.length) {
        return
      }
      this.newPostDefaultContentHtml += '<p>Owners: '
      userToMention.forEach(user => {
        this.newPostDefaultContentHtml += `<span class="mention" data-mention-id="${user.email}">@${user.name}</span> `
      })
      this.newPostDefaultContentHtml += '</p>'
    }
  }
}
</script>

<style scoped>
.forum-header-wrapper {
  margin-top: var(--forum-header-moveup);
  margin-bottom: 0;
  margin-left: -16px;
  margin-right: -16px;
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: var(--color-green-thick);
}
.forum-header {
  margin: 0 auto;
  padding: 16px 24px;
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 60px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.forum-menu {
  margin-left: 48px;
  margin-right: 5px;
  height: 30px;
}
.menu-button {
  height: 32px;
  border: none;
}
.menu-route-forum {
  cursor: pointer;
}

.button-new-post {
  height: 32px;
  border: none;
  margin-left: 5px;
}
.header-icons-wrapper {
  margin: 0;
  width: 30%;
  height: 32px;
  display: flex;
  align-items: center;
}
.header-icon {
  margin-right: 10px;
  color: var(--color-white);
  font-size: 20px;
  cursor: pointer;
}
.forum-search {
  position: relative;
  width: 70%;
  display: flex;
  justify-content: flex-end;
}
.forum-search:hover :focus {
  background-color: var(--color-white);
}
.forum-search >>> .ivu-form-item {
  margin-bottom: 0px !important;
  width: 100%;
  text-align: end;
}
.forum-search >>> .ivu-input {
  border: none;
  background-color: var(--color-light-gray);
}

.forum-search .search-box >>> input {
  margin: 0;
}

.header-icon {
  margin-right: 10px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

@media screen and (min-width: 1024px) {
  .forum-header-wrapper {
    margin-left: -48px;
    margin-right: -48px;
  }

  .forum-header {
    margin: 0 auto;
    padding-left: 8px;
    padding-right: 8px;
    max-width: calc(1024px - 96px);
  }

  .forum-search .search-box {
    width: 600px;
  }
}

@media screen and (min-width: 1440px) {
  .forum-header {
    padding-left: 64px;
    padding-right: 64px;
    max-width: calc(1440px - 96px);
  }
}

@media screen and (min-width: 1560px) {
  .forum-header {
    max-width: 1440px;
  }
}
</style>
